import * as React from "react";

type Props = { children: React.ReactNode };
type State = { error: Error | null; info: React.ErrorInfo | null };

/**
 * App-wide error boundary. Without one, any uncaught render error unmounts the
 * whole tree and leaves a blank white page with only a console trace. This
 * renders the error + component stack on screen so failures are diagnosable
 * (and the app degrades to a readable message instead of nothing).
 */
export class ErrorBoundary extends React.Component<Props, State> {
  state: State = { error: null, info: null };

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    // Keep the component stack so we can see WHICH component threw.
    this.setState({ info });
    // eslint-disable-next-line no-console
    console.error("Uncaught render error:", error, info.componentStack);
  }

  render() {
    const { error, info } = this.state;
    if (!error) return this.props.children;
    return (
      <div style={{ padding: 24, fontFamily: "ui-monospace, monospace", color: "#0f172a" }}>
        <h1 style={{ fontSize: 18, fontWeight: 700, marginBottom: 8 }}>
          Something went wrong rendering this page.
        </h1>
        <p style={{ marginBottom: 12 }}>
          The error below crashed the UI. Reload after it's fixed.
        </p>
        <pre
          style={{
            whiteSpace: "pre-wrap",
            background: "#fff1f2",
            border: "1px solid #fecdd3",
            borderRadius: 8,
            padding: 12,
            fontSize: 12,
            overflow: "auto",
          }}
        >
          {String(error.stack || error.message)}
          {info?.componentStack ? `\n\nComponent stack:${info.componentStack}` : ""}
        </pre>
      </div>
    );
  }
}
